import type { DataProvider } from '@refinedev/core';
import type { AxiosInstance } from 'axios';

import { ENV_VARIABLES } from '@/constants';

import { buildFormData } from './helper';
import { resourceConfig } from './resourceConfig';

export const dataProvider = (axiosInstance: AxiosInstance): DataProvider => ({
  getOne: async ({ resource, id }) => {
    const config = resourceConfig[resource];

    const { data, headers } = await axiosInstance.get(`${config.path}/${id}`);
    return { data, headers };
  },
  getList: async ({ resource, pagination, meta }) => {
    const params: Record<string, string | number> = {};

    const config = resourceConfig[resource];

    const path = meta?.url || config.path;

    if (pagination && pagination.mode !== 'off') {
      params.page = pagination.current || 1;
      params.page_size = pagination.pageSize || 10;
    }

    if (meta?.params) {
      Object.assign(params, meta.params);
    }

    const { data, headers } = await axiosInstance.get(path, {
      params
    });

    return { data, total: data.length, headers };
  },
  create: async params => {
    const { resource, meta, variables } = params;
    const config = resourceConfig[resource];

    const path = meta?.url || config.path;

    // send multipart form if requested
    if (meta?.multipart) {
      const formData = buildFormData(variables as Record<string, unknown>);
      const { data, headers } = await axiosInstance.postForm(path, formData);

      return { data, headers };
    }

    const { data, headers } = await axiosInstance.post(path, variables);

    return { data, headers };
  },
  update: async ({ resource, id, variables, meta }) => {
    const config = resourceConfig[resource];

    const path = meta?.url || config.path;

    // send multipart form if requested
    if (meta?.multipart) {
      const formData = buildFormData(variables as Record<string, unknown>);

      const { data, headers } = await axiosInstance.putForm(
        `${path}/${id}`,
        formData
      );

      return { data, headers };
    }

    const { data, headers } = await axiosInstance.put(
      `${path}/${id}`,
      variables
    );

    return { data, headers };
  },
  deleteOne: async ({ resource, id }) => {
    const config = resourceConfig[resource];

    const { data, headers } = await axiosInstance.delete(
      `${config.path}/${id}`
    );

    return { data, headers };
  },
  custom: async ({ url, method, payload, query, meta }) => {
    const config = resourceConfig[url];
    const path = config?.path ?? url;

    const { data, headers } = await axiosInstance({
      url: path,
      method,
      data: payload,
      params: query ?? meta?.params,
      responseType: meta?.responseType,
      headers: meta?.headers
    });

    return { data, total: data.length, headers };
  },
  getApiUrl: () =>
    axiosInstance.defaults.baseURL ?? ENV_VARIABLES.PANORAMA_API_URL
});
